import {
  applyScopeParams,
  createEmptyScope,
  normalizeScope,
  parseScopeParams,
  toScopeQuery,
} from '../../../utils/scopeSelection';
import { DUPLICATE_FILTER_VALUES } from '../../../constants/violationConstants';
import { filterValueToIso } from '../../../utils/toIsoWithOffset';

/*
 * Фильтр вкладки «События — Список»: URL, запрос списка, подсчёт выборки.
 *
 * Внутри уровня (объекты, камеры, правила…) значения объединяются, между
 * уровнями — пересекаются; считает это backend. Несколько значений принимает
 * только /analytics/events/unified, категорийные списки и count-selected —
 * по одному значению на поле. Поэтому:
 *  - список с одним типом и одиночными значениями идёт категорийным API, как
 *    раньше, иначе — unified;
 *  - подсчёт по фильтру раскладывается на сочетания одиночных значений. У
 *    события ровно один объект, камера, правило и статус, поэтому сочетания не
 *    пересекаются и их счёт можно сложить без дублей.
 */

/** Пары «двух галочек»: обе или ни одной — фильтра нет. */
export const ANALYTICS_GROUP = [
  { value: 'false', label: 'Учитываемые' },
  { value: 'true', label: 'Исключённые' },
];
export const DUPLICATE_GROUP = [
  { value: 'possible_duplicate', label: 'Возможные' },
  { value: 'confirmed_duplicate', label: 'Подтверждённые' },
];
export const STATUS_GROUP = [
  { value: 'active', label: 'Активные' },
  { value: 'resolved', label: 'Завершённые' },
];

const pickOne = (values, group) => {
  const allowed = group.map((option) => option.value);
  const picked = [...new Set((values || []).filter((value) => allowed.includes(value)))];
  return picked.length === 1 ? picked[0] : '';
};

/** Отмеченные галочки → значение фильтра: одно или пусто («все»). */
export const groupValue = (values, group) => pickOne(values, group);
/** Значение фильтра → отмеченные галочки. */
export const groupChecks = (value) => (value ? [value] : []);

export const createEmptyEventFilters = () => ({
  scope: createEmptyScope(),
  types: '',
  status: '',
  analytics: '',
  duplicate: '',
  from: '',
  to: '',
});

// Ссылки из «Сводки» и старые закладки: одиночные параметры прежней схемы.
const LEGACY_PARAMS = [
  ['factoryIds', 'factory'],
  ['workshopIds', 'workshop'],
  ['sectionIds', 'section'],
  ['roomIds', 'room'],
  ['cameraIds', 'camera'],
  ['ruleIds', 'rule'],
];
const FIELD_PARAMS = ['types', 'status', 'analytics', 'duplicate', 'from', 'to'];

export const parseEventFilters = (searchParams) => {
  const params = searchParams || new URLSearchParams();
  const scope = parseScopeParams(params);
  scope.zoneIds = [];
  LEGACY_PARAMS.forEach(([key, param]) => {
    const legacy = params.get(param);
    if (scope[key].length === 0 && legacy) scope[key] = [legacy];
  });

  let analytics = params.get('analytics') || '';
  let duplicate = params.get('duplicate') || '';
  // Прежний единый селект «Аналитика» хранил и состояние дубля.
  if (DUPLICATE_FILTER_VALUES.includes(analytics)) {
    if (!duplicate) duplicate = analytics;
    analytics = '';
  }

  return {
    scope: normalizeScope(scope),
    types: params.get('types') || '',
    status: pickOne([params.get('status')], STATUS_GROUP),
    analytics: pickOne([analytics], ANALYTICS_GROUP),
    duplicate: pickOne([duplicate], DUPLICATE_GROUP),
    from: params.get('from') || '',
    to: params.get('to') || '',
  };
};

/** Фильтр → URL. Старые одиночные параметры удаляются, страница — на первую. */
export const writeEventFilters = (searchParams, filters) => {
  const next = applyScopeParams(searchParams, { ...filters.scope, zoneIds: [] });
  LEGACY_PARAMS.forEach(([, param]) => next.delete(param));
  FIELD_PARAMS.forEach((key) => {
    const value = filters[key];
    if (value) next.set(key, value);
    else next.delete(key);
  });
  next.delete('offset');
  return next;
};

// Порядок отметок не меняет фильтр: снять и снова поставить галочку — не правка.
const sortedScope = (scope) => Object.fromEntries(
  Object.entries(normalizeScope({ ...scope, zoneIds: [] })).map(([key, ids]) => [key, [...ids].sort()])
);

export const eventFiltersKey = (filters) => JSON.stringify({
  ...filters,
  scope: sortedScope(filters.scope),
});

export const isEmptyEventFilters = (filters) =>
  eventFiltersKey(filters) === eventFiltersKey(createEmptyEventFilters());

/**
 * Применённый фильтр → параметры unified (массивы). Это же значение хранит
 * выбор «все по фильтру»: по нему считаются «Посчитать» и «Дубли».
 */
export const buildEventsQuery = (filters, types) => {
  const { zone_ids: _zones, ...scopeQuery } = toScopeQuery(filters.scope);
  const query = { event_types: [...types], ...scopeQuery };
  if (filters.status) query.statuses = [filters.status];
  if (filters.analytics) query.analytics_excluded = filters.analytics;
  if (filters.duplicate) query.duplicate_state = filters.duplicate;
  const from = filterValueToIso(filters.from);
  const to = filterValueToIso(filters.to);
  if (from) query.from = from;
  if (to) query.to = to;
  return query;
};

// Поле unified (массив) ↔ поле категорийного API (одно значение).
const DIMENSIONS = [
  ['factory_ids', 'factory_id'],
  ['workshop_ids', 'workshop_id'],
  ['section_ids', 'section_id'],
  ['room_ids', 'room_id'],
  ['camera_ids', 'camera_id'],
  ['rule_ids', 'rule_id'],
  ['statuses', 'status'],
];
const SCALAR_KEYS = ['analytics_excluded', 'duplicate_state', 'from', 'to'];

const isBlank = (value) => value === '' || value === null || value === undefined;

/** Значения измерения из любой формы фильтра: массив unified или одиночное поле. */
export const valuesOf = (filters, plural, single) => {
  const source = filters || {};
  if (Array.isArray(source[plural]) && source[plural].length > 0) return source[plural];
  return isBlank(source[single]) ? [] : [source[single]];
};

const scalarsOf = (filters) => Object.fromEntries(
  SCALAR_KEYS.filter((key) => !isBlank(filters?.[key])).map((key) => [key, filters[key]])
);

/** Типы событий фильтра. Старый снимок без event_types — только нарушения. */
export const eventTypesOf = (filters) => (
  Array.isArray(filters?.event_types) && filters.event_types.length > 0
    ? filters.event_types
    : ['violation']
);

/**
 * Параметры категорийного списка, если фильтр в него укладывается (не больше
 * одного значения в каждом поле), иначе null.
 */
export const toCategoryParams = (query) => {
  const params = scalarsOf(query);
  for (let i = 0; i < DIMENSIONS.length; i += 1) {
    const [plural, single] = DIMENSIONS[i];
    const values = valuesOf(query, plural, single);
    if (values.length > 1) return null;
    if (values.length === 1) params[single] = values[0];
  }
  return params;
};

export const MAX_COUNT_COMBINATIONS = 12;

/**
 * Фильтр → сочетания одиночных значений (декартово произведение). Больше
 * `max` сочетаний — null: столько запросов подряд подсчёт не делает.
 */
export const expandToSingleFilters = (query, max = MAX_COUNT_COMBINATIONS) => {
  let combos = [scalarsOf(query)];
  for (let i = 0; i < DIMENSIONS.length; i += 1) {
    const [plural, single] = DIMENSIONS[i];
    const values = valuesOf(query, plural, single);
    if (values.length > 0) {
      if (combos.length * values.length > max) return null;
      combos = combos.flatMap((combo) => values.map((value) => ({ ...combo, [single]: value })));
    }
  }
  return combos;
};

export const TOO_MANY_COMBINATIONS_TEXT =
  'В фильтре слишком много сочетаний объектов, камер и правил для подсчёта. Выберите меньше значений или отметьте события галочками.';

/** Тип отмеченного события: без записи — нарушение (выбор, сохранённый до типов). */
export const selectedTypeOf = (types, id) => (types?.[id] === 'business' ? 'business' : 'violation');

/** Отмеченные id по типам событий. */
export const splitIdsByType = (ids, types) => {
  const result = { violation: [], business: [] };
  [...(ids || [])].forEach((id) => {
    const n = Number(id);
    if (Number.isFinite(n)) result[selectedTypeOf(types, id)].push(n);
  });
  return result;
};

const mergeDistribution = (parts) => {
  const present = parts.filter(Boolean);
  if (present.length === 0) return null;
  const total = present.reduce((sum, part) => sum + (Number(part.total) || 0), 0);
  const byKey = new Map();
  present.forEach((part) => (part.slices || []).forEach((slice) => {
    const key = `${slice.category}|${slice.label}`;
    const prev = byKey.get(key);
    const count = Number(slice.violations_count) || 0;
    byKey.set(key, prev ? { ...prev, violations_count: prev.violations_count + count } : { ...slice, violations_count: count });
  }));
  const slices = [...byKey.values()]
    .map((slice) => ({
      ...slice,
      share_percent: total > 0 ? Math.round((slice.violations_count / total) * 1000) / 10 : 0,
    }))
    .sort((a, b) => b.violations_count - a.violations_count);
  return { basis: present[0].basis, total, slices };
};

/** Ответы count-selected по частям выборки → один ответ той же формы. */
export const mergeCountResults = (results) => {
  const list = (results || []).filter(Boolean);
  if (list.length === 0) return null;
  if (list.length === 1) return list[0];
  return {
    selection_mode: list[0].selection_mode,
    selected_count: list.reduce((sum, item) => sum + (Number(item.selected_count) || 0), 0),
    basis: list[0].basis,
    rule_distribution: mergeDistribution(list.map((item) => item.rule_distribution)),
    metric_distribution: mergeDistribution(list.map((item) => item.metric_distribution)),
  };
};

export const COLLECT_PAGE_SIZE = 200;

/**
 * id всех событий списка по фильтру, не больше `cap`. Больше — null: столько
 * id в одно задание не отправляем.
 */
export const collectEventIds = async (listFn, params, cap) => {
  const ids = [];
  let offset = 0;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const response = await listFn({ ...params, limit: COLLECT_PAGE_SIZE, offset });
    const total = Number(response?.total) || 0;
    if (total > cap) return null;
    (response?.items || []).forEach((event) => {
      const n = Number(event.id);
      if (Number.isFinite(n)) ids.push(n);
    });
    offset += COLLECT_PAGE_SIZE;
    if (!response?.items?.length || offset >= total) break;
  }
  return [...new Set(ids)];
};
